import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { StatusBannerCameraDetailsCollectorService } from '@hunting-nature/Secacam/pages/cameras/camera-details/status-banner-camera-details/status-banner-camera-details-collector.service';
import { RemoteControlActions } from '@hunting-nature/Secacam/pages/cameras/redux/remote-control.actions';
import { RemoteControlSelectors } from '@hunting-nature/Secacam/pages/cameras/redux/remote-control.reducer';
import type { BannerConfiguration } from '@hunting-nature/Secacam/shared/components/show-banner/banner-configuration';
import { isLegacyCamera } from '@hunting-nature/Secacam/shared/helper/remote-control.helper';
import { ShopService } from '@hunting-nature/Secacam/shared/services/shop.service';
import { SubscriptionService } from '@hunting-nature/Secacam/shared/services/subscription.service';
import { ToastService } from '@hunting-nature/Secacam/shared/services/toast.service';
import { ADD_CAMERA_ERROR } from '@hunting-nature/shared-core/enums/add-camera-error.enum';
import { AlwaysOnMode, GPS } from '@hunting-nature/shared-core/enums/remote-configs.enum';
import type { CameraLocations } from '@hunting-nature/shared-core/interfaces/camera-locations.interface';
import { createLogger } from '@hunting-nature/shared-core/logging';
import type { CameraViewMode } from '@hunting-nature/shared-core/utils/camera.ui.utility';
import { normalizeError } from '@hunting-nature/shared-core/utils/normalize-error';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, delay, EMPTY, exhaustMap, filter, forkJoin, from, map, of, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';

import { GeneralActions } from '../../../shared/redux/general/general.actions';
import { ClipboardService } from '../../../shared/services/clipboard.service';
import { RestService } from '../../../shared/services/rest-service';
import { SettingsService } from '../../../shared/services/settings.service';
import { StatusBannerService } from '../../../shared/services/status-banner.service';
import type { CameraDetailsData } from '../../../shared/interfaces/camera-details-data.interface';
import { CameraDeletionService } from '../services/camera-deletion.service';
import { CamerasPageService } from '../services/cameras.page.service';
import { RemoteControlService } from '../services/remote-control.service';

import { CameraModelService } from '@hunting-nature/shared-core/services/camera-model.service';
import { getCameraImageId } from '../../../shared/helper/camera.helper';
import { CamerasActions } from './cameras.actions';
import { CameraSelectors } from './cameras.reducer';
import type { CameraSortMode } from '../../gallery/image-gallery/filter/components/sort-mode-sheet/sort-mode.type';
import type { CameraProductType } from '@hunting-nature/shared-core/utils/camera.sort-mode.type';

@Injectable()
export class CamerasEffects {
  // TODO: split effects by sub-domain (overview, details, share, gps, invite, view-mode) to avoid mega files.
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly camerasService = inject(CamerasPageService);
  private readonly remoteControlService = inject(RemoteControlService);
  private readonly restService = inject(RestService);
  private readonly navController = inject(NavController);
  private readonly statusBannerCameraDetailsCollectorService = inject(StatusBannerCameraDetailsCollectorService);
  private readonly settingsService = inject(SettingsService);
  private readonly toastService = inject(ToastService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly shopService = inject(ShopService);
  private readonly statusBannerService = inject(StatusBannerService);
  private readonly cameraModelService = inject(CameraModelService);
  private readonly cameraDeletionService = inject(CameraDeletionService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly log = createLogger('CamerasEffects');

  addCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.addCamera),
      exhaustMap(action => {
        return this.restService.addCamera$(action.activationCode).pipe(
          switchMap(() => [CamerasActions.addCameraSucceeded(), CamerasActions.camerasShown()]),
          catchError((error: unknown) => {
            const errorCode = this.mapResponseToError(error);
            if (errorCode != ADD_CAMERA_ERROR.CODE_USED_OR_INVALID) {
              this.log.error('Adding camera failed', { error });
            }
            return of(CamerasActions.addCameraFailed({ error: errorCode }));
          })
        );
      })
    )
  );

  private mapResponseToError(error: unknown) {
    const status = (error as HttpErrorResponse)?.status;
    if (status === 400) {
      return ADD_CAMERA_ERROR.CODE_USED_OR_INVALID;
    }

    return ADD_CAMERA_ERROR.GENERIC;
  }

  camerasShown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CamerasActions.camerasShown,
        GeneralActions.slotManagerPageEntered,
        // Reloads the overview and its cached response after a camera is gone.
        CamerasActions.deleteCameraSucceeded,
        CamerasActions.removeSharedCameraSucceeded
      ),
      withLatestFrom(this.store.select(CameraSelectors.isCamerasLoaded)),
      // The cache is only read until the cameras are loaded once; after that the store already holds them.
      switchMap(([, isCamerasLoaded]) =>
        this.camerasService.getCamerasOverview$(!isCamerasLoaded).pipe(
          map(camerasOverview => CamerasActions.camerasShownSucceeded(camerasOverview)),
          catchError((error: unknown) => {
            this.log.error('Loading cameras failed', { error });

            return of(
              CamerasActions.camerasShownFailed({
                error,
              })
            );
          })
        )
      )
    )
  );

  refreshCameras$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.refreshCameras),
      switchMap(() =>
        this.camerasService.getCamerasOverview$(false).pipe(
          map(camerasOverview => CamerasActions.refreshCamerasSucceeded(camerasOverview)),
          catchError((error: unknown) => {
            this.log.error('Refreshing cameras failed', { error });
            return of(CamerasActions.refreshCamerasFailed({ error }));
          })
        )
      )
    )
  );

  camerasPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.camerasPageEntered),
      switchMap(() => {
        // Step 1: start each load routine independently so every request can dispatch its own load and success/failure actions as it settles.
        const camerasLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action.
          tap(() => this.store.dispatch(CamerasActions.camerasShown())),
          // Emit the success action as soon as this request returns.
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.camerasShownFailed({ error }));
            return of(false);
          })
        );

        const cameraViewModeLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(CamerasActions.cameraViewModeLoaded())),
          switchMap(() => from(this.settingsService.getCameraViewMode$())),
          // Emit the success action as soon as this request returns.
          tap(viewMode => this.store.dispatch(CamerasActions.cameraViewModeLoadedSucceeded({ viewMode }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.cameraViewModeLoadedFailed({ error }));
            return of(false);
          })
        );

        const cameraSortModeLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(CamerasActions.cameraSortModeLoaded())),
          switchMap(() => from(this.settingsService.getCameraSortMode$())),
          // Emit the success action as soon as this request returns.
          tap(sortMode => this.store.dispatch(CamerasActions.cameraSortModeLoadedSucceeded({ sortMode }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.cameraSortModeLoadedFailed({ error }));
            return of(false);
          })
        );

        const productTypeSortModeLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(CamerasActions.productTypeSortModeLoaded())),
          switchMap(() => from(this.settingsService.getProductTypeSortMode$())),
          // Emit the success action as soon as this request returns.
          tap(productType => this.store.dispatch(CamerasActions.productTypeSortModeLoadedSucceeded({ productType }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.productTypeSortModeLoadedFailed({ error }));
            return of(false);
          })
        );

        const cameraModelsLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(CamerasActions.loadCameraModels())),
          switchMap(() => this.cameraModelService.fetchCameraModels$()),
          // Emit the success action as soon as this request returns.
          tap(models => this.store.dispatch(CamerasActions.loadCameraModelsSucceeded({ models }))),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.loadCameraModelsFailed({ error }));
            return of(false);
          })
        );

        const subscriptionDetailsLoaded$ = of(undefined).pipe(
          // Emit the "loading started" action, then call the service.
          tap(() => this.store.dispatch(CamerasActions.subscriptionAndCreditDetailsLoaded())),
          switchMap(() =>
            forkJoin({
              subscriptionDetails: this.subscriptionService.getSubscriptionDetails$(),
              creditDetails: this.subscriptionService.getPrepaidCreditsDetails$(),
            })
          ),
          // Emit the success action as soon as this request returns.
          tap(({ subscriptionDetails, creditDetails }) =>
            this.store.dispatch(CamerasActions.subscriptionAndCreditDetailsLoadedSucceeded({ subscriptionDetails, creditDetails }))
          ),
          map(() => true),
          catchError((error: unknown) => {
            // Convert errors into a failed action and a boolean result so the page-level forkJoin can still wait for all routines to settle.
            this.store.dispatch(CamerasActions.subscriptionAndCreditDetailsLoadedFailed({ error }));
            return of(false);
          })
        );

        // Step 2: wait until all three routines have settled, then emit the page-level terminal action based on their combined outcome.
        return forkJoin([
          camerasLoaded$,
          cameraViewModeLoaded$,
          cameraSortModeLoaded$,
          productTypeSortModeLoaded$,
          cameraModelsLoaded$,
          subscriptionDetailsLoaded$,
        ]).pipe(
          map(results => (results.every(Boolean) ? CamerasActions.cameraPageEnteredSucceeded() : CamerasActions.cameraPageEnteredFailed())),
          catchError(() => of(CamerasActions.cameraPageEnteredFailed()))
        );
      })
    )
  );

  setCameraViewMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.setCameraViewMode),
      switchMap(({ viewMode }: { viewMode: CameraViewMode }) =>
        from(this.settingsService.setCameraViewMode$(viewMode)).pipe(
          map(() => CamerasActions.setCameraViewModeSucceeded()),
          catchError((error: unknown) => of(CamerasActions.setCameraViewModeFailed({ error })))
        )
      )
    )
  );

  setCameraSortMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.setCameraSortMode),
      switchMap(({ sortMode }: { sortMode: CameraSortMode }) =>
        from(this.settingsService.setCameraSortMode$(sortMode)).pipe(
          map(() => CamerasActions.setCameraSortModeSucceeded()),
          catchError((error: unknown) => of(CamerasActions.setCameraSortModeFailed({ error })))
        )
      )
    )
  );

  setProductTypeSortMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.setProductTypeSortMode),
      switchMap(({ productType }: { productType: CameraProductType }) =>
        from(this.settingsService.setProductTypeSortMode$(productType)).pipe(
          map(() => CamerasActions.setProductTypeSortModeSucceeded()),
          catchError((error: unknown) => of(CamerasActions.setProductTypeSortModeFailed({ error })))
        )
      )
    )
  );

  showCamerasFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.camerasShownFailed, CamerasActions.refreshCamerasFailed),
        tap(() => this.toastService.showTranslatedError('errors.cameras.fetchFailed'))
      ),
    { dispatch: false }
  );

  showCameraDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.showCameraDetails),
      withLatestFrom(this.store.select(CameraSelectors.getCameraDetails)),
      switchMap(([action, cameraDetails]) =>
        this.camerasService.watchCameraDetails$(action.cameraId, cameraDetails).pipe(
          map((data: CameraDetailsData) =>
            CamerasActions.showCameraDetailsSucceeded({
              camera: data.camera,
              cameraLocations: data.cameraLocations,
              gpsActive: data.isGpsActive,
              instantModeActive: data.isInstantModeActive,
              banners: data.banners,
              models: data.models,
            })
          ),
          catchError((error: unknown) => {
            this.log.error('Fetching camera details failed', { error });
            return of(CamerasActions.showCameraDetailsFailed({ error }));
          })
        )
      )
    )
  );

  showCameraDetailsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.showCameraDetailsFailed),
        tap(action => {
          const error = action.error as Error;
          this.toastService.showTranslatedError('errors.cameras.fetchDetailsFailed', { errorMessage: error?.message });
        })
      ),
    { dispatch: false }
  );

  deleteCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.deleteCamera),
      exhaustMap(({ camera }) =>
        this.cameraDeletionService.deleteCamera$(camera).pipe(
          map(() => CamerasActions.deleteCameraSucceeded({ cameraId: camera.id })),
          catchError((error: unknown) => of(CamerasActions.deleteCameraFailed({ cameraId: camera.id, error: normalizeError(error) })))
        )
      )
    )
  );

  deleteCameraFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.deleteCameraFailed),
        tap(action => {
          this.log.error('Deleting camera failed', { cameraId: action.cameraId, error: action.error });
          this.toastService.showTranslatedError('errors.cameras.deleteFailed');
        })
      ),
    { dispatch: false }
  );

  removeSharedCamera$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.removeSharedCamera),
      exhaustMap(({ camera }) =>
        this.cameraDeletionService.removeSharedCamera$(camera).pipe(
          map(() => CamerasActions.removeSharedCameraSucceeded({ cameraId: camera.id })),
          catchError((error: unknown) => of(CamerasActions.removeSharedCameraFailed({ cameraId: camera.id, error: normalizeError(error) })))
        )
      )
    )
  );

  removeSharedCameraFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.removeSharedCameraFailed),
        tap(action => {
          this.log.error('Removing shared camera failed', { cameraId: action.cameraId, error: action.error });
          this.toastService.showTranslatedError('errors.cameras.removeFailed');
        })
      ),
    { dispatch: false }
  );

  cameraDeleted$Navigate = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.deleteCameraSucceeded, CamerasActions.removeSharedCameraSucceeded),
        switchMap(() => this.navController.navigateBack('/home/cameras'))
      ),
    { dispatch: false }
  );

  copyActivationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.copyActivationCode),
      exhaustMap(({ activationCode }) =>
        from(this.clipboardService.copyText$(activationCode)).pipe(
          map(() => CamerasActions.copyActivationCodeSucceeded()),
          catchError((error: unknown) => of(CamerasActions.copyActivationCodeFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  copyActivationCodeSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.copyActivationCodeSucceeded),
        tap(() => this.toastService.showTranslatedInfo('cameras.delete.activationCodeCopied'))
      ),
    { dispatch: false }
  );

  copyActivationCodeFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.copyActivationCodeFailed),
        tap(action => {
          this.log.error('Copying activation code failed', { error: action.error });
          this.toastService.showTranslatedError('errors.cameras.copyActivationCodeFailed');
        })
      ),
    { dispatch: false }
  );

  editCameraName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.editCameraName),
      switchMap(action =>
        this.camerasService.changeCameraNameById$(action.cameraId, action.cameraName).pipe(
          map(() => CamerasActions.editCameraNameSucceeded({ cameraId: action.cameraId, cameraName: action.cameraName })),
          catchError((error: unknown) => {
            return of(
              CamerasActions.editCameraNameFailed({
                cameraId: action.cameraId,
                originalCameraName: action.originalCameraName,
                error: error as HttpErrorResponse,
              })
            );
          })
        )
      )
    )
  );

  editCameraNameFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.editCameraNameFailed),
        tap(() => this.toastService.showTranslatedError('errors.cameras.renameFailed'))
      ),
    { dispatch: false }
  );

  loadCameraGpsLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.loadCameraGpsLocations),
      switchMap(action =>
        this.camerasService.getCameraGpsLocationById$(action.cameraId).pipe(
          map((cameraLocations: CameraLocations) => CamerasActions.loadCameraGpsLocationsSucceeded({ cameraLocations })),
          catchError((error: unknown) =>
            of(
              CamerasActions.loadCameraGpsLocationsFailed({
                cameraId: action.cameraId,
                error: error as HttpErrorResponse,
              })
            )
          )
        )
      )
    )
  );

  loadCameraGpsLocationsFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.loadCameraGpsLocationsFailed),
        tap(() => this.toastService.showTranslatedError('errors.cameras.loadLocationFailed'))
      ),
    { dispatch: false }
  );

  enterCameraDetailsLoadCameraSharedWith$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.enterCameraDetailsLoadCameraShareContacts),
      withLatestFrom(this.store.select(CameraSelectors.getShareContacts)),
      filter(([_, shareContacts]) => !shareContacts || shareContacts.length === 0),
      switchMap(([action]) =>
        this.camerasService.fetchCameraShareContacts$(action.cameraId, 10, 0).pipe(
          map(({ shareContacts, totalCount }) => CamerasActions.enterCameraDetailsLoadCameraShareContactsSucceeded({ shareContacts, totalCount })),
          catchError((error: unknown) => {
            this.toastService.showTranslatedError('errors.cameras.loadShareContactsFailed');
            return of(CamerasActions.enterCameraDetailsLoadCameraShareContactsFailed({ error: normalizeError(error) }));
          })
        )
      )
    )
  );

  activateCameraGps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.activateCameraGps),
      switchMap(action =>
        this.remoteControlService.applyGps$(action.cameraIdentCode, { gps: GPS.ON }).pipe(
          map(() => CamerasActions.activateCameraGpsSucceeded()),
          catchError((error: unknown) =>
            of(
              CamerasActions.activateCameraGpsFailed({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  reloadCameraGps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.reloadCameraGps),
      switchMap(action =>
        this.remoteControlService.reloadGps$(action.cameraIdentCode).pipe(
          map(() => CamerasActions.reloadCameraGpsSucceeded()),
          catchError((error: unknown) =>
            of(
              CamerasActions.reloadCameraGpsFailed({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  openGalleryForCamera$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.openGalleryFromButtonClicked, CamerasActions.openGalleryFromImageClicked),
        tap(
          action =>
            void this.navController.navigateForward([`/home/gallery/${action.cameraId}`], {
              state: {
                cameraFilter: {
                  id: action.cameraId,
                  name: action.cameraName,
                },
              },
            })
        )
      ),
    { dispatch: false }
  );

  updatePauseTransmissionState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.updatePauseTransmissionState),
      exhaustMap(action =>
        this.camerasService.setCameraPauseState$(action.pauseType, action.cameraId, action.cameraName, action.sleepUntil).pipe(
          switchMap(() => [
            CamerasActions.updatePauseTransmissionStateSucceeded({
              pauseType: action.pauseType,
              cameraId: action.cameraId,
              cameraName: action.cameraName,
              sleepUntil: action.sleepUntil,
            }),
          ]),
          catchError((error: unknown) => of(CamerasActions.updatePauseTransmissionStateFailed({ error: normalizeError(error) })))
        )
      )
    )
  );

  updatePauseTransmissionStateSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.updatePauseTransmissionStateSucceeded),
      map(() => GeneralActions.goBackToCameraDetailsFromPauseCamera())
    )
  );

  updatePauseTransmissionStateFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.updatePauseTransmissionStateFailed),
        tap(action => this.toastService.showTranslatedError('errors.cameras.updatePauseTransmissionStateFailed', { errorMessage: action.error.message })) // @todo UI
      ),
    { dispatch: false }
  );

  updateCameraShareContactPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.updateCameraShareContactPermissionRequested),
      exhaustMap(action =>
        this.restService.updateCameraShareContactPermission$(action.contactId, action.cameraId, action.permission).pipe(
          switchMap(() => [
            CamerasActions.updateCameraShareContactPermissionSucceeded({ contactId: action.contactId, permission: action.permission }),
            CamerasActions.openCameraShareInviteSentSheet(),
          ]),
          catchError((error: unknown) =>
            of(
              CamerasActions.updateCameraShareContactPermissionFailed({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  updateCameraShareContactPermissionFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.updateCameraShareContactPermissionFailed),
        tap(() => this.toastService.showTranslatedError('errors.cameras.updateShareContactFailed'))
      ),
    { dispatch: false }
  );

  cameraShareInviteSentBackToContactsClicked$Navigate = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.cameraShareInviteSentBackToContactsClicked),
        switchMap(action => this.navController.navigateBack(['home/cameras', action.cameraId, 'contacts']))
      ),
    { dispatch: false }
  );

  updateFirmware$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.updateFirmware),
      switchMap(action =>
        this.remoteControlService.updateFirmware$(action.identCode).pipe(
          map(() => CamerasActions.updateFirmwareSucceeded()),
          catchError((error: unknown) =>
            of(
              CamerasActions.updateFirmwareFailed({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  updateFirmwareFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.updateFirmwareFailed),
        tap(() => this.toastService.showTranslatedError('cameras.details.firmwareUpdateModal.failed'))
      ),
    { dispatch: false }
  );

  deactivateAlwaysOnMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.deactivateAlwaysOnMode),
      tap(() => this.toastService.showTranslatedInfo('cameras.details.alwaysOnModeDeactivate.toastMessage')),
      map(action => RemoteControlActions.setLocalAlwaysOn({ cameraIdentCode: action.cameraIdentCode, imei: action.imei, enabled: false }))
    )
  );

  requestPicture$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.requestPicture),
      exhaustMap(action => {
        if (!action.cameraIdentCode) {
          return of(CamerasActions.requestPictureFailed({ error: new Error('Camera identification code not found') }));
        }

        return this.remoteControlService.sendInstantPic$(action.cameraIdentCode).pipe(
          map(() => CamerasActions.requestPictureSucceeded()),
          catchError((error: unknown) => of(CamerasActions.requestPictureFailed({ error: normalizeError(error) })))
        );
      })
    )
  );

  requestPictureSucceeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.requestPictureSucceeded),
        tap(() => this.toastService.showTranslatedSuccessMessage$('cameras.details.requestPicture.toastRequested'))
      ),
    { dispatch: false }
  );

  pollCameraImageAfterRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.requestPictureSucceeded),
      withLatestFrom(this.store.select(CameraSelectors.getCameraDetails)),
      switchMap(([, cameraDetails]) => {
        const cameraId = cameraDetails?.id;
        if (!cameraId) {
          return EMPTY;
        }

        return this.camerasService.watchForNewImage$(cameraId, getCameraImageId(cameraDetails)).pipe(
          map(camera => CamerasActions.cameraImageReceived({ camera })),
          takeUntil(this.actions$.pipe(ofType(CamerasActions.cameraDetailsPageLeft, CamerasActions.showCameraDetails)))
        );
      })
    )
  );

  requestPictureFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.requestPictureFailed),
        tap(() => this.toastService.showTranslatedError('errors.cameras.requestPictureFailed'))
      ),
    { dispatch: false }
  );

  changeAlwaysOnMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.changeAlwaysOnMode),
      exhaustMap(action => {
        if (!action.cameraIdentCode) {
          return of(CamerasActions.changeAlwaysOnModeFailed({ error: new Error('Camera identification code not found') }));
        }

        return this.remoteControlService.applyInstantMode$(action.cameraIdentCode, { instantMode: AlwaysOnMode.On }).pipe(
          map(() => CamerasActions.changeAlwaysOnModeSucceeded({ alwaysOn: true })),
          catchError((error: unknown) => of(CamerasActions.changeAlwaysOnModeFailed({ error: normalizeError(error) })))
        );
      })
    )
  );

  changeAlwaysOnModeFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.changeAlwaysOnModeFailed),
        tap(() => this.toastService.showTranslatedError('errors.cameras.changeAlwaysOnModeFailed'))
      ),
    { dispatch: false }
  );

  openAddCreditsSite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.openAddCreditsSite),
        switchMap(() => this.shopService.openSubscriptionManager())
      ),
    { dispatch: false }
  );

  slotManagerPageEntered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.slotManagerPageEntered),
      tap(() => this.store.dispatch(CamerasActions.subscriptionDetailsLoaded())),
      switchMap(() =>
        this.subscriptionService.getSubscriptionDetails$().pipe(
          map(subscriptionDetails => CamerasActions.subscriptionDetailsLoadedSucceeded({ subscriptionDetails })),
          catchError((error: unknown) => of(CamerasActions.subscriptionDetailsLoadedFailed({ error })))
        )
      )
    )
  );

  regenerateCameraDetailsBanners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.goBackToCameraDetailsFromRemoteControl),
      withLatestFrom(this.store.select(CameraSelectors.getCameraDetails), this.store.select(RemoteControlSelectors.instantModeOff)),
      switchMap(([_action, camera, instantModeOff]) => {
        if (!camera) {
          return EMPTY;
        }

        const isLegacy = isLegacyCamera(camera?.model);
        const instantModeActive = !isLegacy && !instantModeOff;
        const banners: Array<BannerConfiguration> = this.statusBannerCameraDetailsCollectorService.collect(camera, instantModeActive);

        return from(this.statusBannerService.getPostponedStatusBanners$()).pipe(
          map(postponedStatusBanners => {
            const filteredBanners = this.statusBannerService.filterAllPostponedBanners(banners, postponedStatusBanners);

            return CamerasActions.refreshCameraDetailsBanners({
              banners: filteredBanners,
            });
          })
        );
      })
    )
  );

  changeAlwaysOnModeSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CamerasActions.changeAlwaysOnModeSucceeded),
      withLatestFrom(this.store.select(CameraSelectors.getCameraDetails)),
      tap(([{ alwaysOn }]) => {
        if (alwaysOn) {
          this.toastService.showTranslatedInfo('cameras.details.requestPicture.activation.toastActivated');
        }
      }),
      switchMap(([{ alwaysOn }, camera]) => {
        if (!camera) {
          return EMPTY;
        }

        const instantModeActive = !isLegacyCamera(camera.model) && alwaysOn;
        const banners: Array<BannerConfiguration> = this.statusBannerCameraDetailsCollectorService.collect(camera, instantModeActive);

        return from(this.statusBannerService.getPostponedStatusBanners$()).pipe(
          map(postponedStatusBanners => {
            const filteredBanners = this.statusBannerService.filterAllPostponedBanners(banners, postponedStatusBanners);

            return CamerasActions.refreshCameraDetailsBanners({
              banners: filteredBanners,
            });
          })
        );
      })
    )
  );

  closeAndNavigateRequestPictureNoSubscriptionModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CamerasActions.closeAndNavigateRequestPictureNoSubscriptionModal),
        delay(100),
        switchMap(action => this.navController.navigateForward(['home/cameras', action.cameraId, action.imei, action.cameraIdentCode, 'remote-control']))
      ),
    { dispatch: false }
  );
}
